/**
 * Container Functions Module
 * Handles the main garden grid functionality and plant placement
 */

// Import plant database loader (full DB loaded on demand)
import { getPlantDatabase } from './plantDatabaseLoader.js';
import { trackDownload } from './analytics.js';
import { getPlantImageUrl } from '../lib/plantImageUrls.js';
import { loadExportLibraries } from './load-export-libs.js';

// Global variables
let selectedPlantLayoutItem = null;
/** Canonical plant name (matches plant database key). */
let selectedBasePlantName = null;
/** When set, palette label and placed cells show this variety name instead of the base name. */
let selectedVariety = null;
let selectedPlantButton = null;
/** Per canonical plant key (base crop): total grid cells for that crop, including all varieties. */
let plantsData = {};
/** Legacy: grid label -> base key; kept for PDF fallback when keys are not canonical. */
let plantBaseByDisplay = {};
/** Row order for summary/PDF: first-seen base plant keys as we scan the grid. */
let summaryPlantRowOrder = [];
let showDetails = false; // For toggling detailed information

// Variables for design alteration functionality
let designMode = false;
let undoHistory = [];
let maxUndoSteps = 20;

let plantButtonLongPressTimer = null;
let plantButtonLongPressTarget = null;
let suppressNextPlantButtonClick = false;
let varietyMenuEl = null;
let varietyMenuOutsideHandler = null;
/** Button the open variety menu is anchored to; used to update fixed positioning on scroll. */
let varietyMenuAnchorButton = null;
/** Removes scroll/resize listeners installed for the open variety menu. */
let varietyMenuRepositionCleanup = null;

const CUSTOM_PLANT_VARIETIES_STORAGE_KEY = 'garden_custom_plant_varieties_v1';

function readCustomVarietiesMap() {
    try {
        const raw = localStorage.getItem(CUSTOM_PLANT_VARIETIES_STORAGE_KEY);
        if (!raw) return {};
        const o = JSON.parse(raw);
        return o && typeof o === 'object' && !Array.isArray(o) ? o : {};
    } catch {
        return {};
    }
}

function getCustomVarietiesForPlant(baseName) {
    const arr = readCustomVarietiesMap()[baseName];
    if (!Array.isArray(arr)) return [];
    return arr.map((s) => String(s).trim()).filter(Boolean);
}

function saveCustomVarietiesForPlant(baseName, list) {
    const map = readCustomVarietiesMap();
    map[baseName] = list;
    try {
        localStorage.setItem(CUSTOM_PLANT_VARIETIES_STORAGE_KEY, JSON.stringify(map));
    } catch {
        showPlantVarietyToast('Could not save custom variety. Storage may be full or blocked.');
    }
}

function appendVarietyMenuItem(menu, button, baseName, label) {
    const item = document.createElement('button');
    item.type = 'button';
    item.className = 'plant-variety-menu-item';
    item.setAttribute('role', 'menuitem');
    item.textContent = label;
    item.addEventListener('click', () => {
        suppressNextPlantButtonClick = true;
        void selectPlantWithVariety(button, baseName, label);
        closeVarietyMenu();
    });
    const draft = menu.querySelector('.plant-variety-menu-add-row');
    if (draft) {
        menu.insertBefore(item, draft);
    } else {
        menu.appendChild(item);
    }
}

function openDraftVarietyRow(menu, button, baseName, builtinVarieties) {
    const existingRow = menu.querySelector('.plant-variety-menu-add-row');
    if (existingRow) {
        const input = existingRow.querySelector('.plant-variety-menu-draft-input');
        if (input) {
            input.focus();
            input.select();
        }
        return;
    }

    const labelsLower = () => {
        const s = new Set(builtinVarieties.map((v) => String(v).trim().toLowerCase()));
        getCustomVarietiesForPlant(baseName).forEach((v) => s.add(String(v).trim().toLowerCase()));
        return s;
    };

    const wrap = document.createElement('div');
    wrap.className = 'plant-variety-menu-add-row';
    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'plant-variety-menu-draft-input';
    input.placeholder = 'New variety…';
    input.setAttribute('aria-label', 'New custom variety name');
    wrap.appendChild(input);
    menu.appendChild(wrap);

    const commit = () => {
        const text = input.value.trim();
        if (!text) {
            wrap.remove();
            return true;
        }
        if (labelsLower().has(text.toLowerCase())) {
            showPlantVarietyToast('That variety is already in the list.');
            input.focus();
            return false;
        }
        const next = getCustomVarietiesForPlant(baseName).concat(text);
        saveCustomVarietiesForPlant(baseName, next);
        wrap.remove();
        appendVarietyMenuItem(menu, button, baseName, text);
        return true;
    };

    input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            commit();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            wrap.remove();
        }
    });

    input.addEventListener('blur', () => {
        setTimeout(() => {
            if (!wrap.isConnected) return;
            if (menu.contains(document.activeElement)) return;
            commit();
        }, 0);
    });

    input.focus();
}

function getPlantButtonBaseName(button) {
    if (!button) return '';
    if (button.dataset.plantName) return button.dataset.plantName.trim();
    const label = button.querySelector('.plant-button-label');
    return (label?.textContent || button.textContent || '').trim();
}

function restorePlantButtonLabel(button) {
    if (!button) return;
    const base = button.dataset.plantName;
    const labelEl = button.querySelector('.plant-button-label');
    if (labelEl && base) labelEl.textContent = base;
}

function showPlantVarietyToast(message) {
    const el = document.createElement('div');
    el.className = 'plant-variety-toast';
    el.textContent = message;
    el.setAttribute('role', 'status');
    el.style.cssText = 'position:fixed;bottom:24px;left:50%;transform:translateX(-50%);padding:10px 18px;border-radius:8px;background:#334155;color:#f8fafc;z-index:10060;font-size:13px;box-shadow:0 4px 12px rgba(0,0,0,0.2);max-width:min(90vw,360px);text-align:center;';
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 3200);
}

function detachVarietyMenuReposition() {
    if (varietyMenuRepositionCleanup) {
        varietyMenuRepositionCleanup();
        varietyMenuRepositionCleanup = null;
    }
    varietyMenuAnchorButton = null;
}

function updateVarietyMenuPosition() {
    const menu = varietyMenuEl;
    const button = varietyMenuAnchorButton;
    if (!menu || !button || !button.isConnected) {
        closeVarietyMenu();
        return;
    }
    const rect = button.getBoundingClientRect();
    menu.style.left = `${Math.max(8, rect.left)}px`;
    menu.style.top = `${rect.bottom + 6}px`;
}

function attachVarietyMenuRepositionListeners(button) {
    detachVarietyMenuReposition();
    varietyMenuAnchorButton = button;

    const onScrollOrResize = () => {
        updateVarietyMenuPosition();
    };

    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);

    const scrollableAncestors = [];
    let el = button.parentElement;
    while (el && el !== document.documentElement) {
        const st = getComputedStyle(el);
        const oy = st.overflowY;
        const ox = st.overflowX;
        const scrollY =
            (oy === 'auto' || oy === 'scroll' || oy === 'overlay') && el.scrollHeight > el.clientHeight + 1;
        const scrollX =
            (ox === 'auto' || ox === 'scroll' || ox === 'overlay') && el.scrollWidth > el.clientWidth + 1;
        if (scrollY || scrollX) {
            el.addEventListener('scroll', onScrollOrResize, true);
            scrollableAncestors.push(el);
        }
        el = el.parentElement;
    }

    const vv = window.visualViewport;
    if (vv) {
        vv.addEventListener('scroll', onScrollOrResize);
        vv.addEventListener('resize', onScrollOrResize);
    }

    varietyMenuRepositionCleanup = () => {
        window.removeEventListener('scroll', onScrollOrResize, true);
        window.removeEventListener('resize', onScrollOrResize);
        scrollableAncestors.forEach((node) => node.removeEventListener('scroll', onScrollOrResize, true));
        if (vv) {
            vv.removeEventListener('scroll', onScrollOrResize);
            vv.removeEventListener('resize', onScrollOrResize);
        }
    };
}

function closeVarietyMenu() {
    detachVarietyMenuReposition();
    if (varietyMenuEl) {
        varietyMenuEl.remove();
        varietyMenuEl = null;
    }
    if (varietyMenuOutsideHandler) {
        document.removeEventListener('pointerdown', varietyMenuOutsideHandler, true);
        varietyMenuOutsideHandler = null;
    }
}

function clearPlantButtonLongPressTimer() {
    if (plantButtonLongPressTimer) {
        clearTimeout(plantButtonLongPressTimer);
        plantButtonLongPressTimer = null;
    }
    plantButtonLongPressTarget = null;
}

async function openVarietyMenu(button, _event) {
    const baseName = getPlantButtonBaseName(button);
    if (!baseName) return;
    const db = await getPlantDatabase();
    const varieties = db[baseName]?.varieties;
    if (!Array.isArray(varieties) || varieties.length === 0) {
        showPlantVarietyToast(`No variety list yet for "${baseName}". Add a "varieties" array in the plant database.`);
        return;
    }
    closeVarietyMenu();
    const menu = document.createElement('div');
    menu.className = 'plant-variety-menu';
    menu.setAttribute('role', 'menu');

    const header = document.createElement('div');
    header.className = 'plant-variety-menu-header';

    const title = document.createElement('div');
    title.className = 'plant-variety-menu-title';
    title.textContent = baseName;

    const addBtn = document.createElement('button');
    addBtn.type = 'button';
    addBtn.className = 'plant-variety-menu-add-btn';
    addBtn.setAttribute('aria-label', 'Add custom variety');
    addBtn.textContent = '+';
    addBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        openDraftVarietyRow(menu, button, baseName, varieties);
    });

    header.appendChild(title);
    header.appendChild(addBtn);
    menu.appendChild(header);

    const builtinLower = new Set(varieties.map((v) => String(v).trim().toLowerCase()));
    const customExtras = getCustomVarietiesForPlant(baseName).filter(
        (c) => !builtinLower.has(String(c).trim().toLowerCase())
    );

    varieties.forEach((v) => appendVarietyMenuItem(menu, button, baseName, v));
    customExtras.forEach((v) => appendVarietyMenuItem(menu, button, baseName, v));

    document.body.appendChild(menu);
    varietyMenuEl = menu;
    attachVarietyMenuRepositionListeners(button);
    updateVarietyMenuPosition();

    varietyMenuOutsideHandler = (e) => {
        if (menu.contains(e.target) || button.contains(e.target)) return;
        closeVarietyMenu();
    };
    setTimeout(() => document.addEventListener('pointerdown', varietyMenuOutsideHandler, true), 0);
}

async function selectPlantWithVariety(button, baseName, varietyName) {
    if (button.classList.contains('plant-blurred')) return;

    if (selectedPlantButton && selectedPlantButton !== button) {
        restorePlantButtonLabel(selectedPlantButton);
        selectedPlantButton.classList.remove('plant-selected');
    }

    const allPestButtons = document.querySelectorAll('.pest-repellent-button');
    allPestButtons.forEach(btn => btn.classList.remove('plant-selected'));

    selectedBasePlantName = baseName;
    selectedVariety = varietyName;
    selectedPlantButton = button;

    const labelEl = button.querySelector('.plant-button-label');
    if (labelEl) labelEl.textContent = varietyName;

    document.querySelectorAll('.plantButton-container .grid-item').forEach(b => b.classList.remove('plant-selected'));
    button.classList.add('plant-selected');

    await updatePlantInfoContainer(baseName, button);
}

// Function to handle the Alter Design button
function setupDesignControls() {
    const alterDesignBtn = document.getElementById('alter-design-btn');
    
    if (!alterDesignBtn) {
        console.warn('Alter design button not found');
        return;
    }
    
    // Toggle design mode when the Alter Design button is clicked
    alterDesignBtn.addEventListener('click', () => {
        designMode = !designMode;
        
        // Update button appearance
        if (designMode) {
            alterDesignBtn.classList.add('active');
            alterDesignBtn.innerHTML = '<i class="fa fa-check"></i> Done';
        } else {
            alterDesignBtn.classList.remove('active');
            alterDesignBtn.innerHTML = '<i class="fa fa-pencil"></i> Modify Design';
        }
        
        // Deselect any selected grid item when entering design mode
        if (selectedPlantLayoutItem) {
            selectedPlantLayoutItem.classList.remove('selected');
            selectedPlantLayoutItem = null;
        }
        
        // Clear plant selection when entering design mode
        if (selectedPlantButton) {
            selectedPlantButton.classList.remove('plant-selected');
            restorePlantButtonLabel(selectedPlantButton);
            selectedBasePlantName = null;
            selectedVariety = null;
            selectedPlantButton = null;
        }
        closeVarietyMenu();
    });
}

// Modify the original setupGridItemListeners function to check for design mode
function setupGridItemListeners() {
    const plantLayoutItems = document.querySelectorAll(".grid-item-5x5");
    
    // Add click event to each plant layout grid item
    plantLayoutItems.forEach((item, index) => {
        item.addEventListener("click", async () => {
            // If in design mode, toggle transparency
            if (designMode) {
                // Toggle transparency
                if (item.classList.contains('transparent')) {
                    item.classList.remove('transparent');
                } else {
                    // Clear the grid item and make it transparent
                    item.innerHTML = '';
                    item.className = 'grid-item-5x5 transparent';
                    item.style.backgroundImage = 'none';
                }
                
                // Update the summary table
                updateSummaryTable();
                
                return; // Early return to avoid normal selection behavior
            }
            
            // New behavior: If a plant is selected, add it to the clicked grid item
            if (selectedBasePlantName && selectedPlantButton) {
                await addPlantToGridItem(item, selectedBasePlantName, selectedVariety, selectedPlantButton);
                return; // Early return to avoid old selection behavior
            }
            
            // If no plant is selected and clicking on an empty grid item, show hint and clear selection
            if (!selectedBasePlantName && !item.querySelector('.plant-label') && !item.classList.contains('transparent')) {
                showGridClickHint(item);
                clearPlantSelection();
                return;
            }
            
            // Old selection behavior (kept for backward compatibility if needed)
            // Deselect previously selected item
            if (selectedPlantLayoutItem) {
                selectedPlantLayoutItem.classList.remove("selected");
            }
            
            // Select the clicked item
            if (selectedPlantLayoutItem === item) {
                // Deselect if clicked again
                selectedPlantLayoutItem = null;
            } else {
                item.classList.add("selected");
                selectedPlantLayoutItem = item;
            }
        });
    });
}

// Function to sort plant buttons alphabetically
function sortPlantButtonsAlphabetically() {
    const plantButtonContainer = document.querySelector('.plantButton-container');
    if (!plantButtonContainer) return;
    
    const plantButtons = Array.from(plantButtonContainer.querySelectorAll('.grid-item'));
    
    // Sort the buttons by canonical plant name (not the temporary variety label)
    plantButtons.sort((a, b) => {
        const textA = getPlantButtonBaseName(a);
        const textB = getPlantButtonBaseName(b);
        return textA.localeCompare(textB);
    });
    
    // Remove all buttons from the container
    plantButtons.forEach(button => button.remove());
    
    // Add them back in the sorted order
    plantButtons.forEach(button => {
        plantButtonContainer.appendChild(button);
    });
}

// Function to filter plant buttons
function filterPlants(category) {
    const plantButtons = document.querySelectorAll('.plantButton-container .grid-item');
    
    plantButtons.forEach(button => {
        const plantName = getPlantButtonBaseName(button);
        
        // Determine which category this plant belongs to
        let plantCategory = 'vegetables'; // Default
        
        // Define herbs
        const herbs = ["Basil", "Bay Leaf", "Borage", "Catnip", 
            "Chamomile", "Chervil", "Chives", "Cilantro", 
            "Coriander", "Culantro", "Dandelion Greens", "Dill", 
            "Fennel Bulb", "Galangal", "Garlic", "Ginger", 
            "Good King Henry", "Horseradish", "Lambsquarters", "Lavender", 
            "Lemon Balm", "Lemongrass", "Lovage", "Marjoram", 
            "Mints", "Mustard", "Nettles", "Oregano", 
            "Parsley", "Rosemary", "Sage", "Savory",
            "Sorrel", "Stevia", "Tarragon", "Caraway", "Cinnamon", "Curry Leaf", "Ginseng", "Peppermint", "Thyme", 
            "Turmeric"];
        
        // Define fruits
        const fruits = ["Bitter Melon", "Cantaloupe", "Ground Cherries", "Pineapple", "Strawberries", "Watermelon"];
        
        // Define flowers
        const flowers = ["Asters", "Begonias", "Black-eyed Susans", "chrysanths", 
            "Clematis", "Columbines", "Coneflowers", "Cosmos", 
            "Daffodils", "Dahlias", "Daisies", "Foxgloves", 
            "Geraniums", "Hollyhocks", "Hostas", "Hydrangeas", 
            "Impatiens", "Irises", "Lilies", "Marigolds", 
            "Morning Glories", "Nasturtiums", "Pansies", "Peace Lily", "Poppy", "Primrose" , "Peonies", 
            "Petunias", "Roses", "Rue", "Snapdragons", "Sunflowers", 
            "Tulips", "Violets", "Wisteria", "Zinnias", "Calendula", "Tansy", "Wormwood"];
        
        // Check which category the plant belongs to
        if (herbs.includes(plantName)) {
            plantCategory = 'herbs';
        } else if (fruits.includes(plantName)) {
            plantCategory = 'fruits';
        } else if (flowers.includes(plantName)) {
            plantCategory = 'flowers';
        }
        
        // Show/hide based on selected category
        if (category === 'all' || category === plantCategory) {
            button.style.display = 'flex';
        } else {
            button.style.display = 'none';
        }
        
        // Re-apply plant selection highlighting if this button was selected
        if (selectedPlantButton === button) {
            button.classList.add('plant-selected');
        }
    });
}

// Function to handle filter button clicks
function setupFilterButtons() {
    const filterButtons = document.querySelectorAll('.filter-btn');
    
    filterButtons.forEach(button => {
        button.addEventListener('click', () => {
            // Remove active class from all buttons
            filterButtons.forEach(btn => btn.classList.remove('active'));
            
            // Add active class to clicked button
            button.classList.add('active');
            
            // Filter plants based on selected category
            filterPlants(button.dataset.filter);
        });
    });
    
    // Herbs then Vegetables so container layout matches post-tab-switch (scroll consistent)
    triggerVegetablesFilterWithScrollReset();
}

// Function to trigger the vegetables filter - can be called by plant auth system
export function triggerVegetablesFilter() {
    // Trigger an actual click event on the vegetables button
    const vegetablesButton = document.querySelector('.filter-btn[data-filter="vegetables"]');
    
    if (vegetablesButton) {
        // Trigger the click event to ensure all event handlers are called
        vegetablesButton.click();
        console.log('✅ Vegetables filter button clicked via triggerVegetablesFilter');
    } else {
        console.warn('⚠️ Vegetables filter button not found');
    }
}

// Briefly activate another tab (e.g. Herbs) then Vegetables so container layout matches
// post-tab-switch state and scroll behavior is consistent (used on load and after sign-in).
export function triggerVegetablesFilterWithScrollReset() {
    const herbsButton = document.querySelector('.filter-btn[data-filter="herbs"]');
    const vegetablesButton = document.querySelector('.filter-btn[data-filter="vegetables"]');
    if (herbsButton && vegetablesButton) {
        herbsButton.click();
        setTimeout(() => {
            vegetablesButton.click();
        }, 20);
    } else if (vegetablesButton) {
        vegetablesButton.click();
    }
}

// Function to clear the garden name
function clearGardenName() {
    const gardenNameInput = document.getElementById('garden-name');
    if (gardenNameInput) {
        gardenNameInput.value = '';
    }
}

// Garden name: single input above layout (no separate setup needed; input is in place)
function setupGardenNameInput() {
    const gardenNameInput = document.getElementById('garden-name');
    if (!gardenNameInput) return;
    // Optional: add/remove placeholder class for styling when empty
    function updatePlaceholderClass() {
        if (gardenNameInput.value.trim()) {
            gardenNameInput.classList.remove('placeholder');
        } else {
            gardenNameInput.classList.add('placeholder');
        }
    }
    gardenNameInput.addEventListener('input', updatePlaceholderClass);
    gardenNameInput.addEventListener('blur', updatePlaceholderClass);
    updatePlaceholderClass();
}

// Function to update the summary table with plant data
async function updateSummaryTable() {
    const tableBody = document.getElementById('plant-summary-body');
    const totalCountElement = document.getElementById('total-plant-count');
    
    if (!tableBody || !totalCountElement) {
        console.warn('Summary table elements not found');
        return;
    }
    
    // Clear the table
    tableBody.innerHTML = '';
    
    // Get all plants from the grid
    const gridItems = document.querySelectorAll('.grid-item-5x5');
    
    // Reset plant counts (one summary row per base crop, not per variety label)
    plantsData = {};
    plantBaseByDisplay = {};
    summaryPlantRowOrder = [];
    let totalPlants = 0;

    gridItems.forEach((item) => {
        const plantLabel = item.querySelector('.plant-label');
        if (!plantLabel) return;
        const displayName = plantLabel.textContent;
        const baseName = (plantLabel.dataset.basePlant || displayName || '').trim() || displayName;
        if (!plantsData[baseName]) {
            plantsData[baseName] = 0;
            summaryPlantRowOrder.push(baseName);
        }
        plantsData[baseName]++;
        totalPlants++;
    });
    
    // If no plants, show message
    if (totalPlants === 0) {
        tableBody.innerHTML = '<tr><td colspan="8" class="empty-table-message">No plants added yet</td></tr>';
        totalCountElement.textContent = '0';
        return;
    }
    
    const db = await getPlantDatabase();
    
    for (const plant of summaryPlantRowOrder) {
        const count = plantsData[plant];
        const row = document.createElement('tr');

        row.innerHTML = `
            <td>${plant}</td>
            <td>${count}</td>
        `;

        if (db[plant]) {
            const details = db[plant];
            row.innerHTML += `
                <td class="detail-column spacing-col">${details.spacing || 'N/A'}</td>
                <td class="detail-column depth-col">${details.depth || 'N/A'}</td>
                <td class="detail-column soil-col">${details.soil || 'N/A'}</td>
                <td class="detail-column sun-col">${details.sun || 'N/A'}</td>
                <td class="detail-column companions-col">${details.companions || 'N/A'}</td>
                <td class="detail-column pests-col">${details.pests || 'N/A'}</td>
            `;
        } else {
            // If plant is not in database, add empty cells
            row.innerHTML += `
                <td class="detail-column spacing-col">N/A</td>
                <td class="detail-column depth-col">N/A</td>
                <td class="detail-column soil-col">N/A</td>
                <td class="detail-column sun-col">N/A</td>
                <td class="detail-column companions-col">N/A</td>
                <td class="detail-column pests-col">N/A</td>
            `;
        }
        
        tableBody.appendChild(row);
    }
    
    // Update total count
    totalCountElement.textContent = totalPlants;
}

// Function to update the plant info container
async function updatePlantInfoContainer(plantName, buttonElement) {
    const plantInfoContainer = document.getElementById('plant-info-container');
    const selectedPlantImage = document.getElementById('selected-plant-image');
    const selectedPlantName = document.getElementById('selected-plant-name');
    const companionPlantsList = document.getElementById('companion-plants-list');
    
    // UI panel is optional; companion highlighting should still work even if the panel was removed from the page.
    const hasInfoPanel = !!(plantInfoContainer && selectedPlantImage && selectedPlantName && companionPlantsList);
    
    // Update the plant name
    if (selectedPlantName) selectedPlantName.textContent = plantName;
    
    // Update the plant image (full WebP URL)
    const buttonClasses = Array.from(buttonElement.classList);
    const imageClass = buttonClasses.find(cls => cls !== 'grid-item');
    if (selectedPlantImage) {
        selectedPlantImage.className = 'plant-image';
        selectedPlantImage.style.backgroundImage = 'none';
    }
    const imageUrl = imageClass ? getPlantImageUrl(imageClass) : null;
    if (imageUrl && selectedPlantImage) selectedPlantImage.style.backgroundImage = `url(${imageUrl})`;
    
    // Update companion plants list
    if (companionPlantsList) companionPlantsList.innerHTML = '';
    
    const db = await getPlantDatabase();
    if (db[plantName] && db[plantName].companions) {
        const companions = db[plantName].companions;
        
        if (companions === 'N/A' || companions === '') {
            if (companionPlantsList) companionPlantsList.textContent = 'No companion plants recommended';
        } else {
            // Split the companions string into individual plants
            // The companions are usually separated by commas
            const companionsList = companions.split(',').map(comp => comp.trim());
            
            // Create elements for each companion plant
            companionsList.forEach(companion => {
                if (!companionPlantsList) return;
                const companionItem = document.createElement('span');
                companionItem.className = 'companion-item';
                companionItem.textContent = companion;
                companionPlantsList.appendChild(companionItem);
            });
        }
    } else {
        if (companionPlantsList) companionPlantsList.textContent = 'No companion data available';
    }
    
    // Show the container
    if (hasInfoPanel) plantInfoContainer.style.display = 'flex';
    
    // Highlight companion plant buttons
    await highlightCompanionPlants(plantName);
}

// Function to highlight companion plant buttons
async function highlightCompanionPlants(plantName) {
    // Reset all buttons to their normal state first
    const allPlantButtons = document.querySelectorAll(".plantButton-container .grid-item");
    allPlantButtons.forEach(button => {
        button.classList.remove('companion-highlight');
        button.classList.remove('tooltip-trigger');
        button.removeAttribute('data-tooltip');
    });
    
    if (!plantName) return;
    const db = await getPlantDatabase();
    if (!db[plantName]) return;
    
    // Get companions string from the database
    const companionsString = db[plantName].companions;
    
    // If no companions, return
    if (!companionsString || companionsString === 'N/A' || companionsString === '') return;
    
    // Split the companions string into an array of companion plants
    const companionsList = companionsString.toLowerCase().split(',').map(comp => comp.trim());
    
    // Loop through all plant buttons and highlight companions
    allPlantButtons.forEach(button => {
        const buttonPlantName = button.textContent.trim().toLowerCase();
        
        // Check if this plant's name is in the companions list
        // We'll check if the button text is included in any companion name
        // or if any companion name includes the button text
        const isCompanion = companionsList.some(companion => 
            companion.includes(buttonPlantName) || buttonPlantName.includes(companion)
        );
        
        if (isCompanion) {
            button.classList.add('companion-highlight');
            button.classList.add('tooltip-trigger');
            // Uses the existing CSS-only tooltip system: `content: attr(data-tooltip)`
            button.setAttribute('data-tooltip', 'Companion');
        }
    });
}

// Function to create and show pest information tooltip (db from caller to avoid async in mouseenter)
function createPestInfoTooltip(plantName, db) {
    if (!db) return null;
    const plantInfo = db[plantName];
    if (!plantInfo || !plantInfo.pests) return null;

    // Create tooltip element
    const tooltip = document.createElement('div');
    tooltip.className = 'pest-info-tooltip';
    
    // Add pest information
    const pests = plantInfo.pests.split(',').map(pest => pest.trim());
    tooltip.innerHTML = `
        <strong>Common Pests:</strong>
        <ul>
            ${pests.map(pest => `<li>${pest}</li>`).join('')}
        </ul>
    `;

    return tooltip;
}

// Function to clear plant selection
function clearPlantSelection() {
    closeVarietyMenu();
    if (selectedPlantButton) {
        restorePlantButtonLabel(selectedPlantButton);
        selectedPlantButton.classList.remove('plant-selected');
        selectedBasePlantName = null;
        selectedVariety = null;
        selectedPlantButton = null;
    }

    // Hide and reset the plant info / companion UI
    const plantInfoContainer = document.getElementById('plant-info-container');
    const selectedPlantImage = document.getElementById('selected-plant-image');
    const selectedPlantName = document.getElementById('selected-plant-name');
    const companionPlantsList = document.getElementById('companion-plants-list');
    if (plantInfoContainer) plantInfoContainer.style.display = 'none';
    if (selectedPlantImage) selectedPlantImage.style.backgroundImage = 'none';
    if (selectedPlantName) selectedPlantName.textContent = 'Select a plant';
    if (companionPlantsList) companionPlantsList.innerHTML = '';
    void highlightCompanionPlants(null);
    
    // Also clear pest repellent button selections
    const allPestButtons = document.querySelectorAll('.pest-repellent-button');
    allPestButtons.forEach(btn => btn.classList.remove('plant-selected'));
}

// Show "Select a plant and click the grid" hint only when user clicks an empty grid square (no plant selected)
let gridClickHintTimeout = null;
let gridClickHintEl = null;

function showGridClickHint(gridItem) {
    // Remove any existing hint
    if (gridClickHintEl && gridClickHintEl.parentNode) {
        gridClickHintEl.remove();
    }
    if (gridClickHintTimeout) clearTimeout(gridClickHintTimeout);

    const hint = 'First, Select a plant and click the grid';
    gridClickHintEl = document.createElement('div');
    gridClickHintEl.className = 'grid-click-hint';
    gridClickHintEl.textContent = hint;
    document.body.appendChild(gridClickHintEl);

    const rect = gridItem.getBoundingClientRect();
    gridClickHintEl.style.left = `${rect.left + rect.width / 2}px`;
    gridClickHintEl.style.top = `${rect.bottom + 6}px`;
    gridClickHintEl.style.transform = 'translateX(-50%)';

    const dismiss = () => {
        if (gridClickHintEl && gridClickHintEl.parentNode) {
            gridClickHintEl.remove();
            gridClickHintEl = null;
        }
        if (gridClickHintTimeout) clearTimeout(gridClickHintTimeout);
        document.removeEventListener('click', dismiss);
    };

    gridClickHintTimeout = setTimeout(dismiss, 2500);
    // Defer so the click that opened the hint doesn't immediately dismiss it
    setTimeout(() => document.addEventListener('click', dismiss, { once: true }), 0);
}

// Function to add a plant to a specific grid item (uses full WebP image, no sprite)
async function addPlantToGridItem(gridItem, basePlantName, variety, plantButton) {
    const db = await getPlantDatabase();
    const buttonClasses = Array.from(plantButton.classList);
    const imageClass = buttonClasses.find(cls => cls !== 'grid-item');
    const displayName = variety && String(variety).trim() ? String(variety).trim() : basePlantName;
    
    gridItem.className = 'grid-item-5x5';
    gridItem.innerHTML = '';
    
    // Full WebP image in grid cell
    const url = imageClass ? getPlantImageUrl(imageClass) : null;
    if (url) {
        const img = document.createElement('img');
        img.src = url;
        img.alt = '';
        img.className = 'plant-icon-img';
        img.loading = 'lazy';
        gridItem.appendChild(img);
    }
    
    const plantLabel = document.createElement('span');
    plantLabel.textContent = displayName;
    plantLabel.className = 'plant-label';
    plantLabel.dataset.basePlant = basePlantName;
    gridItem.appendChild(plantLabel);
    
    if (db[basePlantName] && db[basePlantName].plantsPerSquareFoot) {
        const plantsPerSqft = document.createElement('span');
        plantsPerSqft.className = 'plants-per-sqft';
        let sqftValue = db[basePlantName].plantsPerSquareFoot;
        if (sqftValue === ">1 sqft") {
            plantsPerSqft.textContent = "1/sq";
        } else {
            plantsPerSqft.textContent = sqftValue;
        }
        gridItem.appendChild(plantsPerSqft);
    }

    // Add hover event listeners for pest information (db captured in closure)
    gridItem.addEventListener('mouseenter', (e) => {
        const tooltip = createPestInfoTooltip(basePlantName, db);
        if (tooltip) {
            gridItem.appendChild(tooltip);
        }
    });

    gridItem.addEventListener('mouseleave', () => {
        const tooltip = gridItem.querySelector('.pest-info-tooltip');
        if (tooltip) {
            tooltip.remove();
        }
    });
    
    // Update the summary table
    await updateSummaryTable();
}

function setupPlantButtonListeners() {
    const plantButtons = document.querySelectorAll(".plantButton-container .grid-item");
    
    plantButtons.forEach((button) => {
        button.addEventListener("click", async () => {
            if (suppressNextPlantButtonClick) {
                suppressNextPlantButtonClick = false;
                return;
            }

            // Non-premium: blurred plants are locked; show sign-in/upgrade modal instead of selecting
            if (button.classList.contains('plant-blurred')) {
                if (window.plantAuthManager && typeof window.plantAuthManager.showLockedPlantModal === 'function') {
                    window.plantAuthManager.showLockedPlantModal();
                }
                return;
            }

            // If clicking the same plant button, deselect it
            if (selectedPlantButton === button) {
                clearPlantSelection();
                return;
            }

            const plantName = getPlantButtonBaseName(button);

            if (selectedPlantButton) {
                restorePlantButtonLabel(selectedPlantButton);
                selectedPlantButton.classList.remove('plant-selected');
            }

            const allPestButtons = document.querySelectorAll('.pest-repellent-button');
            allPestButtons.forEach(btn => btn.classList.remove('plant-selected'));

            selectedBasePlantName = plantName;
            selectedVariety = null;
            selectedPlantButton = button;

            const labelEl = button.querySelector('.plant-button-label');
            if (labelEl) labelEl.textContent = plantName;

            await updatePlantInfoContainer(plantName, button);
            button.classList.add('plant-selected');
        });

        button.addEventListener('pointerdown', (e) => {
            if (button.classList.contains('plant-blurred')) return;
            if (e.button !== 0) return;
            clearPlantButtonLongPressTimer();
            plantButtonLongPressTarget = button;
            plantButtonLongPressTimer = window.setTimeout(() => {
                plantButtonLongPressTimer = null;
                plantButtonLongPressTarget = null;
                suppressNextPlantButtonClick = true;
                void openVarietyMenu(button, e);
            }, 550);
        });

        button.addEventListener('pointerup', () => {
            if (plantButtonLongPressTarget === button) clearPlantButtonLongPressTimer();
        });

        button.addEventListener('pointercancel', () => {
            if (plantButtonLongPressTarget === button) clearPlantButtonLongPressTimer();
        });

        button.addEventListener('pointerleave', () => {
            if (plantButtonLongPressTarget === button) clearPlantButtonLongPressTimer();
        });

        button.addEventListener('contextmenu', (e) => {
            if (button.classList.contains('plant-blurred')) return;
            e.preventDefault();
            suppressNextPlantButtonClick = true;
            void openVarietyMenu(button, e);
        });
    });
}

// Function to create grid items dynamically
function createGridItems(total) {
    const grid = document.getElementById("grid");
    if (!grid) {
        console.warn('Grid element not found');
        return;
    }
    
    grid.innerHTML = ""; // Clear existing items
    for (let i = 0; i < total; i++) {
        let div = document.createElement("div");
        div.className = "grid-item-5x5";
        div.style.border = "1px solid black";
        grid.appendChild(div);
    }
    
    // Set up event listeners for the newly created grid items
    setupGridItemListeners();
    setupGridItemRightClickListeners();
}

// Function to clear a grid item when right-clicked
function setupGridItemRightClickListeners() {
    const gridItems = document.querySelectorAll(".grid-item-5x5");
    
    gridItems.forEach(item => {
        item.addEventListener("contextmenu", (e) => {
            e.preventDefault(); // Prevent the default context menu
            
            // Reset the grid item to its original state
            item.innerHTML = "";
            item.className = "grid-item-5x5";
            item.style.backgroundImage = "none";
            
            // Update the summary table
            updateSummaryTable();
            
            return false;
        });
    });
}

// Function to update the grid based on input values
function updateGrid(showConfirmation = false) {
    const lengthInput = document.getElementById("length");
    const widthInput = document.getElementById("width");
    
    if (!lengthInput || !widthInput) {
        console.warn('Grid dimension inputs not found');
        return;
    }
    
    const length = parseInt(lengthInput.value);
    const width = parseInt(widthInput.value);
    
    // Validate input
    if (length <= 0 || width <= 0 || length > 12 || width > 8) {
        alert("Please enter valid dimensions (1-8)");
        return;
    }

    // Only show confirmation modal if called from buttons
    if (showConfirmation) {
        const modal = document.getElementById('confirmation-modal');
        if (modal) {
            modal.style.display = 'flex';
            
            // Set up event listeners for the buttons
            const confirmYes = document.getElementById('confirm-yes');
            const confirmNo = document.getElementById('confirm-no');
            
            if (confirmYes) {
                confirmYes.onclick = function() {
                    modal.style.display = 'none';
                    updateGridSize(length, width);
                };
            }
            
            if (confirmNo) {
                confirmNo.onclick = function() {
                    modal.style.display = 'none';
                };
            }
        }
    } else {
        // Direct update without confirmation
        updateGridSize(length, width);
    }
}

// Helper function to actually update the grid
function updateGridSize(length, width) {
    const grid = document.getElementById("grid");
    if (!grid) return;
    
    // Clear the garden name when bed size changes
    clearGardenName();
    
    // Update CSS grid properties
    const cellSize = window.innerWidth < 480 ? 50 : window.innerWidth < 768 ? 60 : 80;
    grid.style.gridTemplateColumns = `repeat(${length}, ${cellSize}px)`;
    grid.style.gridTemplateRows = `repeat(${width}, ${cellSize}px)`;

    // Update the number of grid items
    createGridItems(length * width);
    
    // Reset the summary table
    updateSummaryTable();
    
    // Clear all shade radius elements when grid is updated
    if (typeof clearAllShadeElements === 'function') {
        clearAllShadeElements();
    }
}

// Function to generate PDF of the garden plan (loads html2canvas + jsPDF on first use)
async function generatePDF() {
    const downloadBtn = document.getElementById('download-pdf-bed') || document.querySelector('[data-download-pdf]');
    const originalHtml = downloadBtn?.innerHTML;
    if (downloadBtn) {
        downloadBtn.disabled = true;
        downloadBtn.innerHTML = '<i class="fa fa-spinner fa-spin"></i> Preparing…';
    }
    try {
        await loadExportLibraries();
    } catch (e) {
        console.error('Failed to load export libraries', e);
        alert('PDF generation is temporarily unavailable. Please try again.');
        if (downloadBtn) { downloadBtn.disabled = false; downloadBtn.innerHTML = originalHtml; }
        return;
    }
    if (!window.jspdf) {
        alert('PDF generation is not available.');
        if (downloadBtn) { downloadBtn.disabled = false; downloadBtn.innerHTML = originalHtml; }
        return;
    }
    const db = await getPlantDatabase();
    const { jsPDF } = window.jspdf;
    const pdf = new jsPDF('p', 'mm', 'a4');
    const gardenNameInput = document.getElementById('garden-name');
    const gardenName = gardenNameInput ? gardenNameInput.value.trim() || "Unnamed Garden" : "Unnamed Garden";
    pdf.setFontSize(20);
    pdf.text(`Garden Layout: "${gardenName}"`, 20, 30);
    const gridElement = document.getElementById('grid');
    if (!gridElement) {
        console.error('Grid element not found');
        if (downloadBtn) { downloadBtn.disabled = false; downloadBtn.innerHTML = originalHtml; }
        return;
    }
    await updateSummaryTable();
    window.html2canvas(gridElement).then(canvas => {
        // Add the grid image to PDF
        const gridImgData = canvas.toDataURL('image/png');
        
        // Calculate aspect ratio to fit on PDF
        const imgWidth = 170; // mm
        const imgHeight = canvas.height * imgWidth / canvas.width;
        
        pdf.addImage(gridImgData, 'PNG', 20, 35, imgWidth, imgHeight);
    
        // Add plant summary
        pdf.text("Garden Plan Summary:", 20, imgHeight + 50);
        // Add horizontal line after summary title
        pdf.setLineWidth(0.5);
        pdf.line(20, imgHeight + 66, 190, imgHeight + 66);

        // Add summary table data
        let yPos = imgHeight + 75; // Increased initial spacing after title line
        const xPos = 20;
        
        // Define column widths - adjusted to accommodate all columns
        const cellWidth = [25, 15, 15, 15, 25, 15, 30, 35]; 
        
        // Calculate total width
        const totalWidth = cellWidth.reduce((sum, width) => sum + width, 0);
        
        // Function to draw table header
        function drawTableHeader(y) {
            let x = xPos;
            pdf.setFontSize(10);
            pdf.setFont(undefined, 'bold');
            
            pdf.text("Plant", x, y);
            x += cellWidth[0];
            
            pdf.text("Count", x, y);
            x += cellWidth[1];
            
            pdf.text("Space", x, y);
            x += cellWidth[2];
            
            pdf.text("Depth", x, y);
            x += cellWidth[3];
            
            pdf.text("Soil", x, y);
            x += cellWidth[4];
            
            pdf.text("Sun", x, y);
            x += cellWidth[5];
            
            pdf.text("Companions", x, y);
            x += cellWidth[6];
            
            pdf.text("Common Pests", x, y);
            
            // Reset font to normal for table contents
            pdf.setFont(undefined, 'normal');
            
            // Add line under header
            y += 3;
            pdf.setLineWidth(0.5);
            pdf.line(xPos, y, xPos + totalWidth, y);
            
            return y + 8; // Return the new Y position after header with increased spacing
        }
        
        // Draw initial table header
        yPos = drawTableHeader(yPos);
        
        // Add table rows
        pdf.setFont(undefined, 'normal');
        
        // Helper function to wrap text and draw it in a cell
        function drawWrappedText(text, x, y, maxWidth, lineHeight) {
            if (!text || text === 'N/A') {
                pdf.text('N/A', x, y);
                return y;
            }
            
            // Split text into words
            const words = text.split(' ');
            let line = '';
            let newY = y;
            
            // Go through words
            for (let i = 0; i < words.length; i++) {
                const testLine = line + words[i] + ' ';
                const testWidth = pdf.getStringUnitWidth(testLine) * pdf.getFontSize() / pdf.internal.scaleFactor;
                
                if (testWidth > maxWidth && i > 0) {
                    // Line is too long, print current line and move to next line
                    pdf.text(line.trim(), x, newY);
                    line = words[i] + ' ';
                    newY += lineHeight;
                } else {
                    line = testLine;
                }
            }
            
            // Print the last line
            if (line.trim() !== '') {
                pdf.text(line.trim(), x, newY);
            }
            
            return newY;
        }
        
        // Check if there are plants
        if (Object.keys(plantsData).length === 0) {
            pdf.text("No plants added to the garden", xPos, yPos);
        } else {
            for (const plant of summaryPlantRowOrder) {
                const count = plantsData[plant];
                let rowStartY = yPos;
                let maxRowHeight = rowStartY;
                
                // Check if we need a new page
                if (yPos > 250) {
                    pdf.addPage();
                    yPos = 20;
                    // Draw header on new page and update yPos
                    yPos = drawTableHeader(yPos);
                    rowStartY = yPos;
                    maxRowHeight = rowStartY;
                }
                
                // Reset X position for new row
                let currentX = xPos;
                
                // Ensure normal font for table contents
                pdf.setFont(undefined, 'normal');
                
                // Plant name - simple text for fixed columns
                pdf.text(plant, currentX, rowStartY);
                currentX += cellWidth[0];
                
                // Count
                pdf.text(count.toString(), currentX, rowStartY);
                currentX += cellWidth[1];
                
                const baseForDb = plantBaseByDisplay[plant] || plant;
                if (db[baseForDb]) {
                    const details = db[baseForDb];
                    
                    // Spacing - simple text
                    pdf.text(details.spacing || 'N/A', currentX, rowStartY);
                    currentX += cellWidth[2];
                    
                    // Depth - simple text
                    pdf.text(details.depth || 'N/A', currentX, rowStartY);
                    currentX += cellWidth[3];
                    
                    // Soil Type - wrapped text
                    let newY = drawWrappedText(
                        details.soil || 'N/A', 
                        currentX, 
                        rowStartY, 
                        cellWidth[4] - 2, // Slight margin
                        4 // Line height in mm
                    );
                    maxRowHeight = Math.max(maxRowHeight, newY);
                    currentX += cellWidth[4];
                    
                    // Sun - wrapped text
                    newY = drawWrappedText(
                        details.sun || 'N/A', 
                        currentX, 
                        rowStartY, 
                        cellWidth[5] - 2,
                        4
                    );
                    maxRowHeight = Math.max(maxRowHeight, newY);
                    currentX += cellWidth[5];
                    
                    // Companions - wrapped text
                    newY = drawWrappedText(
                        details.companions || 'N/A', 
                        currentX, 
                        rowStartY, 
                        cellWidth[6] - 2,
                        4
                    );
                    maxRowHeight = Math.max(maxRowHeight, newY);
                    currentX += cellWidth[6];
                    
                    // Common Pests - wrapped text
                    newY = drawWrappedText(
                        details.pests || 'N/A', 
                        currentX, 
                        rowStartY, 
                        cellWidth[7] - 2,
                        4
                    );
                    maxRowHeight = Math.max(maxRowHeight, newY);
                } else {
                    // If plant not in database, add empty cells
                    for (let i = 0; i < 6; i++) {
                        pdf.text('N/A', currentX, rowStartY);
                        currentX += cellWidth[i + 2];
                    }
                }

                // Calculate row height based on the tallest wrapped text
                // Add more padding to the max height for better spacing
                yPos = maxRowHeight + 10;
                
                // Add horizontal separator line between rows with more space
                pdf.setDrawColor(200, 200, 200); // Light gray color
                pdf.setLineWidth(0.2); // Thinner line for row separators
                pdf.line(xPos, yPos - 5, xPos + totalWidth, yPos - 5);
                pdf.setDrawColor(0, 0, 0); // Reset to black
            }
            
            // Add total row with increased spacing
            yPos += 5;
            pdf.setLineWidth(0.5); // Thicker line for total row
            pdf.line(xPos, yPos, xPos + totalWidth, yPos);
            yPos += 10;
            
            pdf.setFont(undefined, 'bold');
            const totalPlants = Object.values(plantsData).reduce((a, b) => a + b, 0);
            pdf.text("Total Plants:", xPos, yPos);
            pdf.text(totalPlants.toString(), xPos + cellWidth[0], yPos);
        }
        
        // Add date at bottom
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        const today = new Date();
        pdf.text(`Garden Bed Created on: ${today.toLocaleDateString()}`, xPos, 280);
        
        // Save the PDF with the garden name and open in new tab
        const pdfBlob = pdf.output('blob');
        const pdfUrl = URL.createObjectURL(pdfBlob);
        const filename = `garden-plan-${gardenName.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.pdf`;
        
        // Open in new tab
        window.open(pdfUrl, '_blank');
        
        // Also trigger download
        const downloadLink = document.createElement('a');
        downloadLink.href = pdfUrl;
        downloadLink.download = filename;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        trackDownload('pdf', filename);
        
        // Clean up the URL object after a short delay
        setTimeout(() => URL.revokeObjectURL(pdfUrl), 1000);
    }).catch(error => {
        console.error('Error generating PDF:', error);
        alert('Error generating PDF. Please try again.');
    }).finally(() => {
        if (downloadBtn) {
            downloadBtn.disabled = false;
            downloadBtn.innerHTML = originalHtml || '<i class="fa fa-download"></i> Download PDF';
        }
    });
}

// Setup responsive grid adjustments
function setupResponsiveGrid() {
    window.addEventListener('resize', () => {
        const lengthInput = document.getElementById("length");
        const widthInput = document.getElementById("width");
        const grid = document.getElementById("grid");
        
        if (!lengthInput || !widthInput || !grid) return;
        
        const length = parseInt(lengthInput.value);
        const width = parseInt(widthInput.value);
        
        const cellSize = window.innerWidth < 480 ? 50 : window.innerWidth < 768 ? 60 : 75;
        grid.style.gridTemplateColumns = `repeat(${length}, ${cellSize}px)`;
        grid.style.gridTemplateRows = `repeat(${width}, ${cellSize}px)`;
    });
}

// Inject full WebP images into sidebar plant buttons (no sprite)
function injectSidebarPlantImages() {
    const buttons = document.querySelectorAll('.plantButton-container .grid-item');
    buttons.forEach((el) => {
        const cls = Array.from(el.classList).find(c => c !== 'grid-item');
        if (!cls) return;
        const labelText = el.textContent.trim();
        const url = getPlantImageUrl(cls);
        el.innerHTML = '';
        if (url) {
            const img = document.createElement('img');
            img.src = url;
            img.alt = '';
            img.className = 'plant-button-icon';
            img.loading = 'lazy';
            el.appendChild(img);
        }
        const label = document.createElement('span');
        label.className = 'plant-button-label';
        label.textContent = labelText;
        el.appendChild(label);
        el.dataset.plantName = labelText;
    });
}

// Main initialization function
export function initializeContainer() {
    injectSidebarPlantImages();
    // Check if this is a page refresh by looking at sessionStorage
    const isPageRefresh = sessionStorage.getItem('gardenPageLoaded');
    
    // Clear grid if this is a page refresh
    if (isPageRefresh) {
        // Clear grid items
        const gridItems = document.querySelectorAll(".grid-item-5x5");
        gridItems.forEach(item => {
            item.innerHTML = "";
            item.className = "grid-item-5x5";
            item.style.backgroundImage = "none";
        });
        
        // Clear the garden name on page refresh
        clearGardenName();
        
        // Update summary table
        updateSummaryTable();
    }
    
    // Set the flag for next page load
    sessionStorage.setItem('gardenPageLoaded', 'true');
    
    // Initialize design controls
    setupDesignControls();

    // Initialize grid without showing confirmation
    updateGrid(false);
    
    // Set up plant button listeners
    setupPlantButtonListeners();
    
    // Initialize empty summary table
    updateSummaryTable();
    
    // Initialize the filter buttons
    setupFilterButtons();

    // Sort plant buttons alphabetically
    sortPlantButtonsAlphabetically();
    
    // Setup garden name input
    setupGardenNameInput();
    
    // Setup responsive grid
    setupResponsiveGrid();
    
    // Add keyboard shortcut to clear plant selection (Escape key)
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
            clearPlantSelection();
        }
    });
            
    // Set up download buttons
    const downloadBtn = document.getElementById('download-btn');
    if (downloadBtn) {
        downloadBtn.addEventListener('click', generatePDF);
    }
    const downloadPdfBedBtn = document.getElementById('download-pdf-bed');
    if (downloadPdfBedBtn) {
        downloadPdfBedBtn.addEventListener('click', generatePDF);
    }
    
    // Make updateGrid available globally for the HTML buttons
    window.updateGrid = updateGrid;
    
    // Make filterPlants available globally for plant auth integration
    window.filterPlants = filterPlants;
    
    // Make triggerVegetablesFilter available globally for plant auth integration
    window.triggerVegetablesFilter = triggerVegetablesFilter;
    
    console.log('✅ Container functions initialized');
}

// Export additional functions that might be needed by other modules
export { 
    updateGrid, 
    updateSummaryTable, 
    clearPlantSelection, 
    addPlantToGridItem,
    filterPlants,
    generatePDF,
    clearGardenName
}; 